import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Box,
  Title,
  Text,
  Stack,
  Modal,
  Button,
  Group,
  Badge,
  MultiSelect,
  Paper,
  TextInput,
  Table,
  Pagination,
  ActionIcon,
  Tooltip,
  Loader,
  Center,
  Anchor,
  Collapse,
} from "@mantine/core";
import { useDisclosure, useDebouncedValue } from "@mantine/hooks";
import {
  IconSearch,
  IconFilter,
  IconRefresh,
  IconMessageCircle,
  IconChevronDown,
  IconChevronUp,
  IconCheck,
  IconX,
} from "@tabler/icons-react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import {
  useDiscussionQueue,
  useResolvedDiscussionQueue,
} from "../../hooks/useInternalDiscussions";
import {
  DiscussionCategory,
  DiscussionStatus,
  discussionCategoryAbbreviations,
  discussionCategoryColors,
  discussionCategoryIcons,
  discussionCategoryLabels,
  type InternalDiscussion,
} from "../../services/internalDiscussions.service";

dayjs.extend(relativeTime);

// ==================== CONSTANTS ====================

const ITEMS_PER_PAGE = 20;

// Category options for filter (sorted alphabetically by label)
const CATEGORY_OPTIONS = Object.values(DiscussionCategory)
  .map((cat) => ({
    value: cat,
    label: `${discussionCategoryIcons[cat]} ${discussionCategoryLabels[cat]}`,
  }))
  .sort((a, b) => a.label.localeCompare(b.label));

// Status options for filter (for closed discussions - resolved/cancelled)
const STATUS_OPTIONS = [
  { value: DiscussionStatus.RESOLVED, label: "Resolved" },
  { value: DiscussionStatus.CANCELLED, label: "Cancelled" },
].sort((a, b) => a.label.localeCompare(b.label));

// ==================== TYPES ====================

interface DiscussionQueueFilters {
  categories?: DiscussionCategory[];
  sites?: string[];
  statuses?: DiscussionStatus[];
}

// ==================== HELPERS ====================

/**
 * Get the site name from a discussion's case
 * Priority: first specimen source name > procedure name > fallback
 */
function getSiteName(discussion: InternalDiscussion): string {
  // Try first specimen's source
  const firstSpecimen = discussion.case?.specimens?.[0];
  if (firstSpecimen?.source?.name) {
    return firstSpecimen.source.name;
  }
  // Fallback to procedure name
  if (discussion.case?.procedureName) {
    return discussion.case.procedureName;
  }
  if (discussion.case?.procedure?.name) {
    return discussion.case.procedure.name;
  }
  return "—";
}

// ==================== COMPONENT ====================

export default function DiscussionQueuePage() {
  const navigate = useNavigate();

  // Modal states
  const [
    filterModalOpened,
    { open: openFilterModal, close: closeFilterModal },
  ] = useDisclosure(false);

  // Resolved section collapse state (collapsed by default)
  const [resolvedOpen, { toggle: toggleResolved }] = useDisclosure(false);

  // Page state
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [filters, setFilters] = useState<DiscussionQueueFilters>({});
  const [pendingFilters, setPendingFilters] = useState<DiscussionQueueFilters>(
    {}
  );
  const [page, setPage] = useState(1);
  const [resolvedPage, setResolvedPage] = useState(1);

  // Data hooks
  const {
    data: discussions = [],
    isLoading,
    error,
    refetch,
  } = useDiscussionQueue();

  const {
    data: resolvedDiscussions = [],
    isLoading: resolvedLoading,
    refetch: refetchResolved,
  } = useResolvedDiscussionQueue();

  // Compute unique site options for filter (from both pending and resolved)
  const siteOptions = useMemo(() => {
    const sites = new Set<string>();
    [...discussions, ...resolvedDiscussions].forEach((d) => {
      const site = getSiteName(d);
      if (site !== "—") {
        sites.add(site);
      }
    });
    return Array.from(sites)
      .map((site) => ({ value: site, label: site }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [discussions, resolvedDiscussions]);

  // Filter discussions
  const filteredDiscussions = useMemo(() => {
    let result = discussions;

    // Apply category filter
    if (filters.categories && filters.categories.length > 0) {
      result = result.filter(
        (d) => d.category && filters.categories!.includes(d.category)
      );
    }

    // Apply site filter
    if (filters.sites && filters.sites.length > 0) {
      result = result.filter((d) => filters.sites!.includes(getSiteName(d)));
    }

    // Apply text search
    if (debouncedSearch) {
      const searchLower = debouncedSearch.toLowerCase();
      result = result.filter((discussion) => {
        const caseNumber = discussion.case?.caseNumber || "";
        const patientName = discussion.case?.patient
          ? `${discussion.case.patient.firstName} ${discussion.case.patient.lastName}`
          : "";
        const initiatedBy = discussion.initiatedBy?.fullName || "";
        const siteName = getSiteName(discussion);
        const category = discussion.category
          ? discussionCategoryLabels[discussion.category]
          : "";
        const reason = discussion.reason || "";

        return (
          caseNumber.toLowerCase().includes(searchLower) ||
          patientName.toLowerCase().includes(searchLower) ||
          initiatedBy.toLowerCase().includes(searchLower) ||
          siteName.toLowerCase().includes(searchLower) ||
          category.toLowerCase().includes(searchLower) ||
          reason.toLowerCase().includes(searchLower)
        );
      });
    }

    return result;
  }, [discussions, filters, debouncedSearch]);

  // Filter resolved discussions (shared filter logic)
  const filteredResolvedDiscussions = useMemo(() => {
    let result = resolvedDiscussions;

    // Apply category filter
    if (filters.categories && filters.categories.length > 0) {
      result = result.filter(
        (d) => d.category && filters.categories!.includes(d.category)
      );
    }

    // Apply site filter
    if (filters.sites && filters.sites.length > 0) {
      result = result.filter((d) => filters.sites!.includes(getSiteName(d)));
    }

    // Apply status filter (for closed discussions)
    if (filters.statuses && filters.statuses.length > 0) {
      result = result.filter((d) => filters.statuses!.includes(d.status));
    }

    // Apply text search
    if (debouncedSearch) {
      const searchLower = debouncedSearch.toLowerCase();
      result = result.filter((discussion) => {
        const caseNumber = discussion.case?.caseNumber || "";
        const patientName = discussion.case?.patient
          ? `${discussion.case.patient.firstName} ${discussion.case.patient.lastName}`
          : "";
        const closedBy =
          discussion.status === DiscussionStatus.CANCELLED
            ? discussion.cancelledBy?.fullName || ""
            : discussion.resolvedBy?.fullName || "";
        const siteName = getSiteName(discussion);
        const category = discussion.category
          ? discussionCategoryLabels[discussion.category]
          : "";
        const notes =
          discussion.status === DiscussionStatus.CANCELLED
            ? discussion.cancellationReason || ""
            : discussion.resolution || "";

        return (
          caseNumber.toLowerCase().includes(searchLower) ||
          patientName.toLowerCase().includes(searchLower) ||
          closedBy.toLowerCase().includes(searchLower) ||
          siteName.toLowerCase().includes(searchLower) ||
          category.toLowerCase().includes(searchLower) ||
          notes.toLowerCase().includes(searchLower)
        );
      });
    }

    return result;
  }, [resolvedDiscussions, filters, debouncedSearch]);

  // Pagination
  const totalPages = Math.ceil(filteredDiscussions.length / ITEMS_PER_PAGE);
  const paginatedData = useMemo(() => {
    const start = (page - 1) * ITEMS_PER_PAGE;
    return filteredDiscussions.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredDiscussions, page]);

  // Resolved pagination
  const resolvedTotalPages = Math.ceil(
    filteredResolvedDiscussions.length / ITEMS_PER_PAGE
  );
  const paginatedResolvedData = useMemo(() => {
    const start = (resolvedPage - 1) * ITEMS_PER_PAGE;
    return filteredResolvedDiscussions.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredResolvedDiscussions, resolvedPage]);

  // Check if any filters are active
  const hasActiveFilters =
    (filters.categories && filters.categories.length > 0) ||
    (filters.sites && filters.sites.length > 0) ||
    (filters.statuses && filters.statuses.length > 0);

  // Handlers
  const handleOpenDiscussion = (discussion: InternalDiscussion) => {
    // For closed discussions (resolved/cancelled), pass the discussionId to view that specific discussion
    if (
      discussion.status === DiscussionStatus.RESOLVED ||
      discussion.status === DiscussionStatus.CANCELLED
    ) {
      navigate(`/discussion-queue/${discussion.caseId}/${discussion.id}`);
    } else {
      navigate(`/discussion-queue/${discussion.caseId}`);
    }
  };

  const handleOpenFilterModal = () => {
    setPendingFilters({ ...filters });
    openFilterModal();
  };

  const handleApplyFilters = () => {
    setFilters({ ...pendingFilters });
    setPage(1);
    closeFilterModal();
  };

  const handleClearPendingFilters = () => {
    setPendingFilters({});
  };

  const handleClearAllFilters = () => {
    setFilters({});
    setPendingFilters({});
    setPage(1);
    setResolvedPage(1);
  };

  const handleRefreshAll = () => {
    refetch();
    refetchResolved();
  };

  return (
    <Container fluid py="xl">
      <Box style={{ maxWidth: "1600px", margin: "0 auto" }}>
        <Stack gap="lg">
          {/* Header */}
          <Group justify="space-between" align="flex-start">
            <div>
              <Title order={2}>
                <Group gap="xs">
                  <IconMessageCircle size={28} />
                  Discussion Queue
                </Group>
              </Title>
              <Text size="sm" c="dimmed" mt="xs">
                Cases flagged for internal discussion and peer review
              </Text>
            </div>
          </Group>

          {/* Search and Filters Row */}
          <Group align="stretch" grow>
            {/* Search Section */}
            <Paper p="md" withBorder style={{ flex: 2 }}>
              <Stack gap="sm">
                <Group>
                  <IconSearch size={20} />
                  <Text fw={500}>Search</Text>
                </Group>
                <TextInput
                  placeholder="Search by case #, patient name, site, or flagged by..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  leftSection={<IconSearch size={16} />}
                />
              </Stack>
            </Paper>

            {/* Filters Section */}
            <Paper p="md" withBorder style={{ flex: 1 }}>
              <Stack gap="sm">
                <Group justify="space-between">
                  <Group>
                    <IconFilter size={20} />
                    <Text fw={500}>Filters</Text>
                  </Group>
                  <Tooltip label="Refresh">
                    <ActionIcon
                      variant="subtle"
                      onClick={handleRefreshAll}
                      loading={isLoading || resolvedLoading}
                    >
                      <IconRefresh size={16} />
                    </ActionIcon>
                  </Tooltip>
                </Group>
                <Button
                  variant="light"
                  onClick={handleOpenFilterModal}
                  fullWidth
                >
                  {hasActiveFilters ? "Edit Filters" : "Add Filters"}
                </Button>
                {hasActiveFilters && (
                  <Group gap="xs" wrap="wrap">
                    <Text size="sm" c="dimmed">
                      Active:
                    </Text>
                    {filters.categories && filters.categories.length > 0 && (
                      <Badge variant="light" color="grape">
                        {filters.categories.length} Categor
                        {filters.categories.length > 1 ? "ies" : "y"}
                      </Badge>
                    )}
                    {filters.sites && filters.sites.length > 0 && (
                      <Badge variant="light" color="teal">
                        {filters.sites.length} Site
                        {filters.sites.length > 1 ? "s" : ""}
                      </Badge>
                    )}
                    {filters.statuses && filters.statuses.length > 0 && (
                      <Badge variant="light" color="blue">
                        {filters.statuses.length} Status
                        {filters.statuses.length > 1 ? "es" : ""}
                      </Badge>
                    )}
                    <Button
                      variant="subtle"
                      size="compact-xs"
                      color="gray"
                      onClick={handleClearAllFilters}
                    >
                      Clear All
                    </Button>
                  </Group>
                )}
                <Text size="sm" c="dimmed">
                  {filteredDiscussions.length} pending discussion
                  {filteredDiscussions.length !== 1 ? "s" : ""}
                </Text>
              </Stack>
            </Paper>
          </Group>

          {/* Table Section */}
          <Paper p="md" withBorder>
            <Stack gap="md">
              {/* Table Header with Pagination */}
              <Group justify="space-between">
                <Text fw={500} size="lg">
                  Discussions ({filteredDiscussions.length})
                </Text>
                {totalPages > 1 && (
                  <Group gap="md">
                    <Text size="sm" c="dimmed">
                      Showing {(page - 1) * ITEMS_PER_PAGE + 1}-
                      {Math.min(
                        page * ITEMS_PER_PAGE,
                        filteredDiscussions.length
                      )}{" "}
                      of {filteredDiscussions.length}
                    </Text>
                    <Pagination
                      total={totalPages}
                      value={page}
                      onChange={setPage}
                      size="sm"
                    />
                  </Group>
                )}
              </Group>

              {/* Table */}
              {isLoading ? (
                <Center p="xl">
                  <Loader />
                </Center>
              ) : error ? (
                <Text c="red" ta="center" py="xl">
                  {error.message || "Failed to load discussion queue"}
                </Text>
              ) : filteredDiscussions.length === 0 ? (
                <Center py="xl">
                  <Stack align="center" gap="xs">
                    <IconMessageCircle size={48} opacity={0.3} />
                    <Text c="dimmed">
                      {hasActiveFilters || search
                        ? "No discussions match the current filters"
                        : "No cases flagged for discussion"}
                    </Text>
                  </Stack>
                </Center>
              ) : (
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th style={{ width: 130 }}>Category</Table.Th>
                      <Table.Th>Case #</Table.Th>
                      <Table.Th>Site</Table.Th>
                      <Table.Th style={{ minWidth: 200 }}>Comment</Table.Th>
                      <Table.Th>Flagged By</Table.Th>
                      <Table.Th style={{ width: 120 }}>Age</Table.Th>
                      <Table.Th style={{ width: 100 }}>Actions</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {paginatedData.map((discussion) => (
                      <Table.Tr key={discussion.id}>
                        {/* Category */}
                        <Table.Td>
                          {discussion.category ? (
                            <Badge
                              color={
                                discussionCategoryColors[discussion.category]
                              }
                              variant="light"
                              leftSection={
                                discussionCategoryIcons[discussion.category]
                              }
                            >
                              {
                                discussionCategoryAbbreviations[
                                  discussion.category
                                ]
                              }
                            </Badge>
                          ) : (
                            <Text c="dimmed" size="sm">
                              —
                            </Text>
                          )}
                        </Table.Td>

                        {/* Case # */}
                        <Table.Td>
                          <Group gap="xs">
                            <Anchor
                              fw={500}
                              size="sm"
                              onClick={() => handleOpenDiscussion(discussion)}
                            >
                              {discussion.case?.caseNumber || "—"}
                            </Anchor>
                            {discussion.isUrgent && (
                              <Badge color="red" size="xs">
                                URGENT
                              </Badge>
                            )}
                          </Group>
                        </Table.Td>

                        {/* Site */}
                        <Table.Td>
                          <Text size="sm">{getSiteName(discussion)}</Text>
                        </Table.Td>

                        {/* Comment */}
                        <Table.Td>
                          <Text
                            size="sm"
                            lineClamp={1}
                            title={discussion.reason}
                          >
                            {discussion.reason || "—"}
                          </Text>
                        </Table.Td>

                        {/* Flagged By */}
                        <Table.Td>
                          <Text size="sm">
                            {discussion.initiatedBy?.fullName || "—"}
                          </Text>
                        </Table.Td>

                        {/* Age */}
                        <Table.Td>
                          <Text size="sm" c="dimmed">
                            {dayjs(discussion.createdAt).fromNow(true)}
                          </Text>
                        </Table.Td>

                        {/* Actions */}
                        <Table.Td>
                          <Button
                            variant="light"
                            size="compact-xs"
                            onClick={() => handleOpenDiscussion(discussion)}
                          >
                            View
                          </Button>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              )}
            </Stack>
          </Paper>

          {/* Resolved Discussions Section */}
          <Paper p="md" withBorder>
            {/* Clickable Header */}
            <Group
              justify="space-between"
              onClick={toggleResolved}
              style={{ cursor: "pointer" }}
            >
              <Group gap="sm">
                <IconCheck size={20} color="var(--mantine-color-green-6)" />
                <Text fw={500} size="lg">
                  Closed Discussions
                </Text>
                <Badge color="gray" variant="light">
                  {filteredResolvedDiscussions.length}
                </Badge>
              </Group>
              <ActionIcon variant="subtle" size="sm">
                {resolvedOpen ? (
                  <IconChevronUp size={16} />
                ) : (
                  <IconChevronDown size={16} />
                )}
              </ActionIcon>
            </Group>

            <Collapse in={resolvedOpen}>
              <Stack gap="md" mt="md">
                {/* Pagination header */}
                {resolvedTotalPages > 1 && (
                  <Group justify="flex-end">
                    <Text size="sm" c="dimmed">
                      Showing {(resolvedPage - 1) * ITEMS_PER_PAGE + 1}-
                      {Math.min(
                        resolvedPage * ITEMS_PER_PAGE,
                        filteredResolvedDiscussions.length
                      )}{" "}
                      of {filteredResolvedDiscussions.length}
                    </Text>
                    <Pagination
                      total={resolvedTotalPages}
                      value={resolvedPage}
                      onChange={setResolvedPage}
                      size="sm"
                    />
                  </Group>
                )}

                {/* Resolved Table */}
                {resolvedLoading ? (
                  <Center p="xl">
                    <Loader />
                  </Center>
                ) : filteredResolvedDiscussions.length === 0 ? (
                  <Center py="xl">
                    <Stack align="center" gap="xs">
                      <IconCheck size={48} opacity={0.3} />
                      <Text c="dimmed">
                        {hasActiveFilters || search
                          ? "No closed discussions match the current filters"
                          : "No closed discussions"}
                      </Text>
                    </Stack>
                  </Center>
                ) : (
                  <Table striped highlightOnHover>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th style={{ width: 130 }}>Category</Table.Th>
                        <Table.Th style={{ width: 140 }}>Status</Table.Th>
                        <Table.Th>Case #</Table.Th>
                        <Table.Th>Site</Table.Th>
                        <Table.Th style={{ minWidth: 200 }}>Notes</Table.Th>
                        <Table.Th style={{ minWidth: 150 }}>Closed By</Table.Th>
                        <Table.Th style={{ width: 100 }}>Actions</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {paginatedResolvedData.map((discussion) => {
                        const isCancelled =
                          discussion.status === DiscussionStatus.CANCELLED;
                        const closedBy = isCancelled
                          ? discussion.cancelledBy?.fullName
                          : discussion.resolvedBy?.fullName;
                        const closedAt = isCancelled
                          ? discussion.cancelledAt
                          : discussion.resolvedAt;
                        const notes = isCancelled
                          ? discussion.cancellationReason
                          : discussion.resolution;

                        return (
                          <Table.Tr key={discussion.id}>
                            {/* Category */}
                            <Table.Td>
                              {discussion.category ? (
                                <Badge
                                  color={
                                    discussionCategoryColors[
                                      discussion.category
                                    ]
                                  }
                                  variant="light"
                                  leftSection={
                                    discussionCategoryIcons[discussion.category]
                                  }
                                >
                                  {
                                    discussionCategoryAbbreviations[
                                      discussion.category
                                    ]
                                  }
                                </Badge>
                              ) : (
                                <Text c="dimmed" size="sm">
                                  —
                                </Text>
                              )}
                            </Table.Td>

                            {/* Status */}
                            <Table.Td>
                              {isCancelled ? (
                                <Badge
                                  color="red"
                                  variant="light"
                                  leftSection={<IconX size={12} />}
                                >
                                  Cancelled
                                </Badge>
                              ) : (
                                <Badge
                                  color="green"
                                  variant="light"
                                  leftSection={<IconCheck size={12} />}
                                >
                                  Resolved
                                </Badge>
                              )}
                            </Table.Td>

                            {/* Case # */}
                            <Table.Td>
                              <Anchor
                                fw={500}
                                size="sm"
                                onClick={() => handleOpenDiscussion(discussion)}
                              >
                                {discussion.case?.caseNumber || "—"}
                              </Anchor>
                            </Table.Td>

                            {/* Site */}
                            <Table.Td>
                              <Text size="sm">{getSiteName(discussion)}</Text>
                            </Table.Td>

                            {/* Notes (Resolution or Cancellation Reason) */}
                            <Table.Td>
                              <Text
                                size="sm"
                                lineClamp={1}
                                title={notes || undefined}
                              >
                                {notes || "—"}
                              </Text>
                            </Table.Td>

                            {/* Closed By + When */}
                            <Table.Td>
                              <Stack gap={0}>
                                <Text size="sm">{closedBy || "—"}</Text>
                                <Text size="xs" c="dimmed">
                                  {closedAt ? dayjs(closedAt).fromNow() : ""}
                                </Text>
                              </Stack>
                            </Table.Td>

                            {/* Actions */}
                            <Table.Td>
                              <Button
                                variant="light"
                                size="compact-xs"
                                onClick={() => handleOpenDiscussion(discussion)}
                              >
                                View
                              </Button>
                            </Table.Td>
                          </Table.Tr>
                        );
                      })}
                    </Table.Tbody>
                  </Table>
                )}
              </Stack>
            </Collapse>
          </Paper>
        </Stack>

        {/* Filter Modal */}
        <Modal
          opened={filterModalOpened}
          onClose={closeFilterModal}
          title="Filter Discussions"
          size="md"
          closeOnClickOutside={false}
        >
          <Stack gap="md">
            <MultiSelect
              label="Categories"
              placeholder="Select categories..."
              data={CATEGORY_OPTIONS}
              value={pendingFilters.categories || []}
              onChange={(values) =>
                setPendingFilters({
                  ...pendingFilters,
                  categories:
                    values.length > 0
                      ? (values as DiscussionCategory[])
                      : undefined,
                })
              }
              searchable
              clearable
            />

            <MultiSelect
              label="Status"
              description="Filter closed discussions by status"
              placeholder="Select statuses..."
              data={STATUS_OPTIONS}
              value={pendingFilters.statuses || []}
              onChange={(values) =>
                setPendingFilters({
                  ...pendingFilters,
                  statuses:
                    values.length > 0
                      ? (values as DiscussionStatus[])
                      : undefined,
                })
              }
              clearable
            />

            <MultiSelect
              label="Site"
              placeholder="Select sites..."
              data={siteOptions}
              value={pendingFilters.sites || []}
              onChange={(values) =>
                setPendingFilters({
                  ...pendingFilters,
                  sites: values.length > 0 ? values : undefined,
                })
              }
              searchable
              clearable
            />

            <Group justify="space-between" mt="md">
              <Button variant="subtle" onClick={handleClearPendingFilters}>
                Clear All
              </Button>
              <Button onClick={handleApplyFilters}>Apply Filters</Button>
            </Group>
          </Stack>
        </Modal>
      </Box>
    </Container>
  );
}
